import Landing from '@containers/landing/landing.container';
import ForgotPasswordContainer from '@containers/forgot-password/forgot-password.container';
import LoginContainer from '@containers/login/login.container';
import SignupContainer from '@containers/signup/signup.container';
import CampaignContainer from '@containers/campaign/campaign.container';
import InviteContainer from '@containers/invite/invite.container';
import MagicContainer from '@containers/magic/magic.container';
import * as CommonActions from '@services/common/common.action';
import * as UserActions from '@services/user/user.actions';
import * as CallActions from '@services/calls/calls.actions';
import * as FileActions from '@services/file/file.actions';
import {openArticle} from '@services/article/article.actions';
import {IArticleTarget} from '@app-types/article.types';
import {articleTargetForLink} from '@containers/knowledge/articleNavigation';
import * as React from "react";
import {connect} from 'react-redux';
import {RouteConfig} from 'react-router-config';
import {Redirect, Route, RouteComponentProps, Switch, withRouter} from 'react-router-dom';
import {compose} from 'redux';
import {Theme, withTheme} from '@material-ui/core/styles';
import PrivateRouter from './private.router';
import PublicRouterSnackbar from './public.router.snackbar';
import {IUser} from "@app-types/api/user.types";
import { IStuffBeingUploaded } from '@app-types/websocket/file.types';
import Deeplinking from './Deeplinking';
import { getSelectedFamilyResource } from '@services/user/user.utils';
import GmailConnectPage from '@components/integrations/gmail/GmailConnectPage';
import {
  DEFAULT_AUTHENTICATED_PATH,
  GMAIL_CONNECT_RETURN_PATH,
  SETTINGS_PATH,
  getSafeReturnPath,
  gmailConnectLoginState,
} from './safeReturn';
import { isGmailConnectPath } from '../integrations/gmail/gmailAttemptVault';
import { showAlreadyLoggedInNotice } from '@services/accountLink/accountLink.actions';
import AlreadyLoggedInDialog from '@components/common/alreadyLoggedIn/AlreadyLoggedInDialog';
import { isAccountLinkPath, isNewAccountLink } from './accountLinks';
import { loggedOutOnly } from './loggedOutOnly';
import { runCordovaPluginCall } from '@helpers/cordovaBridge';

declare const window: any;
declare const navigator: any;
declare const cordova: any;

interface IState {
  deviceReady: boolean;
}

interface IProps extends RouteComponentProps {
  authToken: string | undefined;
  language: string;
  myFamilyId: number;
  user: IUser;
  isOnline: boolean;
  theme: Theme;

  uploadSlots:IStuffBeingUploaded[],
  uploadFile:(upload:IStuffBeingUploaded) => void;
  uploadAIImage:(data:{imageData:Blob, auth:string, familyId:number, action:string}) => void

  bootstrap: (payload: any) => void;
  selectLanguage: (language: string) => void;
  callReceived:(data:number) => void
  openArticle: (target: IArticleTarget) => void;
  showAlreadyLoggedInNotice: () => void;
}

export const routes: RouteConfig[] = [
  {
    component: Landing,
    exact: true,
    path: '/'
  },
  {
    component: PrivateRouter,
    path: '/app',
  },
  {
    component: LoginContainer,
    exact: true,
    path: '/login',
  },
  {
    component: GmailConnectPage,
    exact: true,
    path: GMAIL_CONNECT_RETURN_PATH,
  },
  {
    component: loggedOutOnly(MagicContainer),
    exact: false,
    path: '/magic/:token',
  },
  {
    component: SignupContainer,
    exact: true,
    path: '/signup',
  },
  {
    component: loggedOutOnly(SignupContainer),
    exact: false,
    path: '/signup/:token',
  },
  {
    component: loggedOutOnly(InviteContainer),
    exact: false,
    path: '/inv/:token',
  },
  {
    component: ForgotPasswordContainer,
    exact: true,
    path: '/forgot-password',
  },
  {
    component: CampaignContainer,
    exact: true,
    path: '/install/:token',
  },
  {
    component: CampaignContainer,
    exact: true,
    path: '/install',
  },
];

// Module-level so it survives PublicRouter remounts (e.g. ErrorBoundary "Try Again"):
// plugins like cordova.openwith throw if init() is called twice, and "deviceready"
// fires again synchronously for every new listener.
let nativeSetupDone: boolean = false;
// The openwith handler is registered once, so it reads login state from the
// router that is mounted now rather than from the one that registered it.
let mountedRouter: PublicRouter | undefined;

export class PublicRouter extends React.Component<IProps, IState> {

  private filesUploaded :number[] = [];

  constructor(props: IProps) {
    super(props);

    this.state = {
      deviceReady: false,
    };

    if (!props.language || props.language.length < 2) {

      const lan = navigator.language? (navigator.language.toLowerCase()) : "";

      if (lan.startsWith("sv")) {
        props.selectLanguage("SE");
      } else if (lan.startsWith("en")) {
        props.selectLanguage("EN");
      } else {
        props.selectLanguage("EN");
      }
    
    } else {
      props.selectLanguage(props.language);
    }

  }

  public componentDidUpdate(prevProps: any) {
    if (this.props.uploadSlots !== prevProps.uploadSlots) {
      for (const upload of this.props.uploadSlots) {
        if (!this.filesUploaded.includes(upload.id)) {
          this.filesUploaded.push(upload.id);
          this.props.uploadFile(upload);
        }
      }
    }
    if (prevProps.authToken && !this.props.authToken) {
      const shouldReturnToGmail = isGmailConnectPath(this.props.location.pathname)
        || getSafeReturnPath(this.props.location.state) === GMAIL_CONNECT_RETURN_PATH;
      this.props.history.replace(
        '/login',
        shouldReturnToGmail ? gmailConnectLoginState() : undefined
      );
      return;
    }
    if (this.props.authToken && prevProps.authToken !== this.props.authToken
      && (this.props.location.pathname === "/"
        || this.props.location.pathname.startsWith("/signup")
        || this.props.location.pathname.startsWith("/login"))
    ) {
      this.props.history.replace(
        getSafeReturnPath(this.props.location.state) || DEFAULT_AUTHENTICATED_PATH
      );
      return;
    }
    if (this.props.location !== prevProps.location) {
      const elmnt = document.getElementById("page-top");
      if (elmnt !== null) {
        elmnt.scrollIntoView({behavior: 'auto', block: 'start'});
      }
    }
    this.updateStatusbar();
  }

  public updateStatusbar() {
    if (!this.state.deviceReady) return;
    const path = this.props.location.pathname || "";

    let editingCalendar = false;
    let onboarding = false;
    const login = path === "/" || path === "" ;

    if (!login) {
      editingCalendar = /^\/app\/calendar\/\d+\/\d+$/.test(path);
      onboarding = path.includes("onboarding") || path.startsWith("/login") ||
        path.startsWith("/signup") ||
        path.startsWith("/forgot-password");
    }

    /* Status bar surface tracks the visible background of each section.
       Hex values mirror the light/dark CSS variables in src/index.css —
       keep them in sync if the palette is tuned.

       Text style: light content when the surface behind the bar is
       dark (dark theme everywhere, or the purple edit header), else
       the platform default (dark icons). */
    // The theme in use, which also follows the phone when the dark mode setting is off.
    const dark = this.props.theme.palette.type === 'dark';
    /* Dark-mode bar surface: a warmer beige-tinted dark for onboarding
       to match its softer feel, neutral dark grey everywhere else. */
    const darkOnboardingBar = "#2A2520";
    const darkBarColor = "#28282b";
    const color = editingCalendar
      ? "#5d395f"                          // purple900 — same in both modes
      : onboarding
        ? (dark ? darkOnboardingBar : "#f4efe9")
        : login
          ? (dark ? darkBarColor : "#f6f4f7")
          : (dark ? darkBarColor : "#f5f5f5");

    const darkText = !editingCalendar && !dark;

    const sb = (window as any).StatusBar;
    if (!sb) {
      console.warn("StatusBar plugin not available (skipping).");
      return;
    }

    runCordovaPluginCall("StatusBar.update", () => {
      sb.overlaysWebView(false);
      sb.backgroundColorByHexString(color);
      /* From Android 15 window.setStatusBarColor, which the plugin uses, does
         nothing: the bar is cordova-android's own view behind it, painted by
         SystemBarPlugin. Cordova's statusBar.setBackgroundColor would hand the
         call back to the plugin, so reach SystemBarPlugin directly. */
      const cdv = (window as any).cordova;
      if (cdv && cdv.platformId === "android" && typeof cdv.exec === "function") {
        const rgb = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
        cdv.exec(null, null, "SystemBarPlugin", "setStatusBarBackgroundColor", [...rgb, 1]);
      }

      if (darkText) sb.styleDefault();
      else sb.styleLightContent();
    });
  }



  public componentDidMount() {
    mountedRouter = this;

    document.addEventListener("deviceready", () => {
      this.setState({ deviceReady: true }, () => {
        this.updateStatusbar();
      });

      if (nativeSetupDone) { return; }
      nativeSetupDone = true;

      this.setupOpenwith();
      runCordovaPluginCall("SplashScreen.hide", () => navigator.splashscreen.hide());

      const FirebasePlugin = (globalThis as any).FirebasePlugin;
      if (FirebasePlugin === undefined || FirebasePlugin === null) {
        console.error("FirebasePlugin is undefined");
      } else  {
        runCordovaPluginCall("FirebasePlugin.onMessageReceived", () => FirebasePlugin.onMessageReceived((message: any) => {
         try {
          if (message.tap && message.tap === 'background') {

            if (this.props.myFamilyId === 0) {
              console.log("we should probably stop receiving these notes!");
            } else if (message.familyId && (Number(message.familyId) !== this.props.myFamilyId)
              && this.props.myFamilyId && this.props.myFamilyId !== 0) {
              alert("You received a notification for group (" + message.familyId + ")." +
                " You are currently logged in to (" + this.props.myFamilyId + ")");
            } else if (message.callId && message.chatId) {
              this.props.callReceived(Number(message.callId));
              this.props.history.push(`/app/messages/${message.chatId}`)
            } else if (message.chatId) {
              this.props.history.push(`/app/messages/${message.chatId}`)
            } else if (message.scheduleId) {
              if (message.suggestionId) {
                this.props.history.push(`/app/calendar/${message.scheduleId}/${message.suggestionId}`)
              } else {
                this.props.history.push(`/app/calendar/${message.scheduleId}/0`)
              }
            } else if (message.noteId) {
              this.props.history.push(`/app/notes/1`)
            } else if (message.todoId) {
              this.props.history.push(`/app/notes/2`)
            } else if (message.packed) {
              this.props.history.push(`/app/notes/0`)
            } else if (message.postId) {
              this.props.history.push(`/app/info/:tab`)
            } else if (message.childInfo) {
              this.props.history.push(`/app/info/:tab`)
            } else if (message.articleId) {
              this.props.openArticle({reference: String(message.articleId)});
            } else if (message.eventId) {
              this.props.history.push(`/app/calendar/event/${message.eventId}`)
            } else if (message.purchaseId) {
              this.props.history.push(`/app/economy/p${message.purchaseId}`)
            } else if (message.transferId) {
              this.props.history.push(`/app/economy/t${message.transferId}`)
            } else if (message.agreementId) {
              this.props.history.push(`/app/economy/a${message.agreementId}`)
            }
          }
         } catch (err) {
           (window as any).reportHandledError?.('Notification tap handler', err, {
             tap: message?.tap,
             messageKeys: message && typeof message === 'object' ? Object.keys(message) : undefined,
             notificationFamilyId: message?.familyId,
             myFamilyId: this.props.myFamilyId,
           });
         }

        }));
      }
    },
      false
    );

    this.props.bootstrap({
      meta: {
        history: this.props.history,
        location: this.props.location
      }
    });
  }

  // called in deviceready code!
  public setupOpenwith() {
    // Increase verbosity if you need more logs
    // cordova.openwith.setVerbosity(cordova.openwith.DEBUG);
    const initSuccess = () => { console.log('init success!'); }
    const initError = (err: any) => { console.log('init failed: ' + err); }

    if(!cordova.openwith) {
      console.error("cordova.openwith plugin not available");
      return;
    }

    // Initialize the plugin
    // openwith sets initCalled before its first native call. Replaying init()
    // after a bridge failure would throw "init should only be called once".
    runCordovaPluginCall("OpenWith.init", () => cordova.openwith.init(initSuccess, initError),
      undefined, { retryOnBridgeError: false });
    const myHandler = (intent: any) => {
     try {
      if (intent === undefined) {
        // alert("there is no intent!")
        return;
      }

      if (intent.action === "VIEW" && intent.items && intent.items.length === 1) {
        const article = typeof intent.items[0].uri === 'string' ? articleTargetForLink(intent.items[0].uri) : undefined;
        if (article) {
          this.props.openArticle(article);
          return;
        }
        if (intent.items[0].uri !== undefined && typeof intent.items[0].uri === 'string' 
          && (intent.items[0].uri.startsWith("https://varannanvecka.app/") || intent.items[0].uri.startsWith("https://co-parenting.app/"))) {
            const path:string = intent.items[0].uri.replace("https://varannanvecka.app", "").replace("https://co-parenting.app", "");
            const router = mountedRouter || this;
            if (isAccountLinkPath(path)) {
              const isNew = isNewAccountLink(path);
              if (router.props.authToken) {
                if (isNew) {
                  router.props.history.push(SETTINGS_PATH);
                  router.props.showAlreadyLoggedInNotice();
                }
                return;
              }
            }
            router.props.history.push(path);
            return;
        }
      }

      if (!this.props.authToken) {
        alert("You must log in before you can share stuff to the app! ");
        return;
      }

      console.log('  action: ' + intent.action); // type of action requested by the user
      console.log('  exit: ' + intent.exit); // if true, you should exit the app after processing

      if (intent.items && intent.items.length > 0) {
        cordova.openwith.load(intent.items[0], (data: string, descriptor: any) => {

          if (descriptor.type !== undefined && typeof descriptor.type === 'string' && descriptor.type.toLowerCase().includes("image")) {
            console.log("the image size: " + data.length + " bytes");

            let action:string = "events";
            if (intent.items[0].text && intent.items[0].text.includes("rec")) {
              action = "receipt";
            } else if (intent.items[0].text && intent.items[0].text.includes("im")) {
              action = "image";
            }

            const img = new Image();
            img.onload = () => {
              // Get the original dimensions from the loaded image
              const originalWidth = img.width;
              const originalHeight = img.height;
              
              // Calculate the scale factor so that neither width nor height exceeds 800
              const scaleFactor = Math.min(800 / originalWidth, 800 / originalHeight);
              
              // Compute the new dimensions
              const newWidth = originalWidth * scaleFactor;
              const newHeight = originalHeight * scaleFactor;
              
              // Create a new canvas with the computed dimensions
              const scaledCanvas = document.createElement('canvas');
              scaledCanvas.width = newWidth;
              scaledCanvas.height = newHeight;
              
              const scaledCtx = scaledCanvas.getContext('2d');
              if (!scaledCtx) {
                alert('Your browser does not support canvas');
                return;
              }
              
              // Draw the image scaled on the new canvas
              scaledCtx.drawImage(img, 0, 0, originalWidth, originalHeight, 0, 0, newWidth, newHeight);
              
              // Convert the canvas to a PNG data URL and extract the binary data 
              scaledCanvas.toBlob((blob) => {
                if (this.props.authToken && blob !== null) {
                  console.log("Uploading blob with size:", blob.size, "bytes");

                  this.props.uploadAIImage({
                    imageData: blob,
                    auth: this.props.authToken,
                    familyId: this.props.myFamilyId,
                    action
                  });

                  if (action === "image" ) { 
                    console.log("image uploaded");
                    setTimeout(() => {this.props.history.push(`/app/info/:tab`);}, 500);
                  } else if (action === "receipt" && this.props.isOnline) {
                    console.log("receipt uploaded");
                    setTimeout(() => {this.props.history.push(`/app/economy/:transactionId`);}, 500);
                  } else if (action === "events") {
                    console.log("events uploaded");
                    setTimeout(() => {this.props.history.push(`/app/calendar/:calendarId/:suggestionId`);}, 500);
                  }
                }
              }, 'image/png');



            };

            // fallback if the mime-type is incorrect.
            const type = descriptor.type.toLowerCase().startsWith("image")?descriptor.type.toLowerCase():"image/png";

            // Set the image source with the proper prefix
            img.src = 'data:'+type+';base64,' + data;
            // data is a long base64 string with the content of the file
          }
          
          
          // this.uploadToServer(item);

          // "exit" when done.
          // Note that there is no need to wait for the upload to finish,
          // the app can continue while in background.
          if (intent.exit) { cordova.openwith.exit(); }
        }, (err: any, descriptor: any) => {
          console.error("Failed to load shared content", err, descriptor);
          alert("We couldn't read the shared file. Please try again with a local photo or file.");
          if (intent.exit) { cordova.openwith.exit(); }
        });
      }
      else {
        alert("intent.items.length == 0");
        if (intent.exit) { cordova.openwith.exit(); }
      }
     } catch (err) {
       (window as any).reportHandledError?.('Share sheet (openwith) handler', err, {
         action: intent?.action,
         itemCount: intent?.items && Array.isArray(intent.items) ? intent.items.length : undefined,
       });
     }
    }
    // Define your file handler
    cordova.openwith.addHandler(myHandler);
  }

  public render() {
    const isSensitiveRoute = isGmailConnectPath(this.props.location.pathname);

    return (
      <>
        <Switch>
          {
            routes.map((route: RouteConfig, index: number) => <Route {...route} key={index}/>)
          }
          <Redirect from="/.well-known/assetlinks.json" to="//varannanvecka.app/.well-known/assetlinks.json"/>
          <Redirect from="/.well-known/apple-app-site-association"
                    to="//varannanvecka.app/.well-known/apple-app-site-association"/>
          {/* Catch-all route for unmatched paths */}
          <Route render={() => <Redirect to="/login" />} />
        </Switch>
        {!isSensitiveRoute && <PublicRouterSnackbar/>}
        {!isSensitiveRoute && <Deeplinking/>}
        <AlreadyLoggedInDialog/>
      </>
    );
  }

}

function mapStateToProps(state: any) {
  let myFamilyId = 0;
  const myFam = getSelectedFamilyResource(state.user?.session);
  if (myFam?.id !== undefined) {
    myFamilyId = myFam.id;
  }

  const sessionData = state.user?.session?.data ?? {};
  const auth = sessionData.authorization;
  const authToken = (typeof auth === "string" && auth.length > 0) ? auth : undefined;
  return {
    isOnline:state.connection.loadCompleted,
    language: state.user.language,
    authToken,
    myFamilyId,
    uploadSlots:state.file.uploadSlots?state.file.uploadSlots:[],
  };

}

const mapDispatchToProps = {
  bootstrap: CommonActions.bootstrap,
  selectLanguage: UserActions.selectLanguage,
  uploadFile: FileActions.internalUploadFileAfterGivenSlot,
  uploadAIImage : UserActions.uploadOfflineEventAnalysis, // should only be used for ext sharing
  callReceived: CallActions.callNotification,
  openArticle,
  showAlreadyLoggedInNotice,
};

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps), withTheme)(PublicRouter);



// WEBPACK FOOTER //
// ./src/router/public.router.tsx